import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { Formik } from "formik";
import { useState } from "react";
import { createEditSolutionFormValues } from "../../../../edit/form/defaultValues";
import type { EditSolutionFormValues } from "../../../../edit/form/types";
import { validateSection, getValidationIssues } from "./validateSection";
import type { SectionEditDialogProps } from "../../types";

const submissionErrorMessage = "Unable to save changes. Please try again.";

function SectionEditDialog({
  section,
  title,
  testId,
  maxWidth,
  details,
  loading,
  error,
  onClose,
  onSubmit,
  children,
  validate = validateSection,
}: SectionEditDialogProps) {
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  if (!section) return null;

  const closeModal = () => {
    setSubmissionError(null);
    onClose();
  };

  return (
    <Dialog
      open
      onClose={loading ? undefined : closeModal}
      fullWidth
      maxWidth={maxWidth}
      data-testid={testId}
    >
      <Formik<EditSolutionFormValues>
        key={`${section}-${details.id}`}
        initialValues={createEditSolutionFormValues(details)}
        validate={validate}
        validateOnMount
        onSubmit={async (values, helpers) => {
          setSubmissionError(null);
          try {
            const succeeded = await onSubmit(values);
            if (succeeded) closeModal();
            else setSubmissionError(submissionErrorMessage);
          } catch {
            setSubmissionError(submissionErrorMessage);
          } finally {
            helpers.setSubmitting(false);
          }
        }}
      >
        {({
          handleSubmit,
          errors,
          isValid,
          isSubmitting,
          isValidating,
          submitCount,
        }) => {
          const submitting = loading || isSubmitting;
          const savingBlocked = submitting || isValidating || !isValid;
          const validationIssues = getValidationIssues(errors);
          const displayedSubmissionError =
            submitCount > 0 ? (error ?? submissionError) : submissionError;
          return (
            <Box component="form" onSubmit={handleSubmit}>
              <DialogTitle>Edit {title}</DialogTitle>
              <DialogContent dividers>
                {displayedSubmissionError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {displayedSubmissionError}
                  </Alert>
                )}
                {!isValid && validationIssues.length > 0 && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    Save is unavailable until the following fields are
                    corrected. Some fields may need to be updated in the main
                    solution edit form:
                    <Box component="ul" sx={{ my: 1, pl: 3 }}>
                      {validationIssues.map((issue) => (
                        <li key={issue}>{issue}</li>
                      ))}
                    </Box>
                  </Alert>
                )}
                {children}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={closeModal}
                  disabled={submitting}
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={savingBlocked}
                  loading={submitting}
                  variant="contained"
                >
                  Save
                </Button>
              </DialogActions>
            </Box>
          );
        }}
      </Formik>
    </Dialog>
  );
}

export default SectionEditDialog;
